/**
 * /tools/cve-lookup — login-gated (any plan).
 * Reuses the ZeroConf HAL for vendor/model dropdowns; queries NVD via backend.
 */
import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    AlertTriangle, ArrowLeft, ArrowRight, ExternalLink, Search, Loader2,
    ShieldCheck, FileText, Calendar, Phone,
} from "lucide-react";
import { getJSON, postJSON } from "../lib/api";
import { usePageMeta } from "../lib/usePageMeta";
import { ToolPageMeta } from "../components/ToolPageMeta";
import { toast } from "sonner";
import { useAuthStore } from "../state/authStore";

const SEV_TONE = {
    critical: { bg: "bg-accent-red/15", border: "border-accent-red/40", text: "text-accent-red", color: "#EF4444" },
    high:     { bg: "bg-accent-amber/15", border: "border-accent-amber/40", text: "text-accent-amber", color: "#FFD100" },
    medium:   { bg: "bg-accent-cyan/15", border: "border-accent-cyan/40", text: "text-accent-cyan", color: "#03D8F3" },
    low:      { bg: "bg-white/[0.06]", border: "border-white/[0.12]", text: "text-text-muted", color: "#a1a1aa" },
    unknown:  { bg: "bg-white/[0.04]", border: "border-white/[0.08]", text: "text-text-dim", color: "#52525b" },
};

const SeverityPill = ({ sev, score }) => {
    const t = SEV_TONE[sev] || SEV_TONE.unknown;
    return (
        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10.5px] font-mono uppercase tracking-[0.18em] border ${t.bg} ${t.border} ${t.text}`}>
            {sev}{score != null && <span className="opacity-70">· {score}</span>}
        </span>
    );
};

const CveCard = ({ cve }) => {
    const [open, setOpen] = useState(false);
    const t = SEV_TONE[cve.severity] || SEV_TONE.unknown;
    const isHigh = cve.severity === "critical" || cve.severity === "high";
    return (
        <div className={`rounded-lg border ${t.border} bg-surface/60 p-4`} data-testid={`cve-${cve.cve_id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2.5 flex-wrap min-w-0">
                    <a href={`https://nvd.nist.gov/vuln/detail/${cve.cve_id}`} target="_blank" rel="noreferrer"
                        className="font-mono text-[13px] text-accent-cyan hover:underline inline-flex items-center gap-1">
                        {cve.cve_id} <ExternalLink className="w-3 h-3" />
                    </a>
                    <SeverityPill sev={cve.severity} score={cve.cvss_v3_score} />
                    <span className="text-[10.5px] font-mono text-text-dim">{cve.published}</span>
                </div>
                <span className={`font-display font-semibold text-2xl ${t.text}`} style={{ minWidth: "3rem", textAlign: "right" }}>
                    {cve.cvss_v3_score ?? "—"}
                </span>
            </div>
            <p className="mt-2 text-[13px] text-text-primary font-medium leading-snug">{cve.title}</p>
            <p className={`mt-2 text-[12.5px] text-text-muted leading-relaxed ${open ? "" : "line-clamp-2"}`}>
                {cve.description}
            </p>
            {(cve.description?.length > 140 || cve.references?.length > 0) && (
                <button type="button" onClick={() => setOpen((v) => !v)}
                    className="mt-2 text-[11.5px] text-accent-cyan hover:underline">
                    {open ? "Show less" : "Read more"}
                </button>
            )}
            {open && cve.references?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-white/[0.04] space-y-1">
                    {cve.references.slice(0, 5).map((r, i) => (
                        <a key={i} href={r} target="_blank" rel="noreferrer"
                            className="block text-[11.5px] text-text-muted hover:text-accent-cyan font-mono truncate">
                            <ExternalLink className="inline w-3 h-3 mr-1" /> {r}
                        </a>
                    ))}
                </div>
            )}
            {isHigh && (
                <Link to="/contact" data-testid={`cve-${cve.cve_id}-consult`}
                    className="mt-3 inline-flex items-center gap-1.5 text-[11.5px] text-accent-cyan hover:underline">
                    <Phone className="w-3 h-3" /> Need help patching? Book a consultation
                </Link>
            )}
        </div>
    );
};

const VENDOR_CARDS = [
    { code: "fortinet",   label: "Fortinet" },
    { code: "zyxel",      label: "Zyxel ZLD" },
    { code: "zyxel_uos",  label: "Zyxel uOS" },
    { code: "watchguard", label: "WatchGuard" },
    { code: "pfsense",    label: "pfSense" },
];

export const CveLookup = () => {
    const { user } = useAuthStore();
    const [hardware, setHardware] = useState(null);
    const [vendor, setVendor] = useState("");
    const [modelId, setModelId] = useState("");
    const [modelName, setModelName] = useState("");
    const [version, setVersion] = useState("");
    const [versions, setVersions] = useState([]);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [gateEmail, setGateEmail] = useState("");
    const [gateName, setGateName] = useState("");
    const [gateOptin, setGateOptin] = useState(true);
    const [unlocking, setUnlocking] = useState(false);

    usePageMeta({
        title: "Firewall CVE Lookup — Fortinet, WatchGuard, Zyxel, pfSense",
        description: "Check your firewall firmware against the official NVD database. Live CVE scoring with CVSS v3 for Fortinet FortiOS, WatchGuard Fireware, Zyxel ZLD/uOS, and pfSense.",
        jsonLd: {
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Firewall CVE Lookup",
            "applicationCategory": "SecurityApplication",
            "operatingSystem": "Web",
            "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
        },
    });

    useEffect(() => {
        postJSON("/tools/usage", {
            tool_id: "cve-lookup", action: "page_view",
        }).catch(() => {});
        (async () => {
            try {
                const d = await getJSON("/zeroconf/hardware");
                setHardware(d);
            } catch (e) {
                console.error(e);
            }
        })();
    }, []);

    const currentVendorBlock = useMemo(
        () => hardware?.vendors?.find((v) => v.vendorCode === vendor),
        [hardware, vendor],
    );

    useEffect(() => {
        if (!modelId) { setVersions([]); return; }
        (async () => {
            try {
                const d = await getJSON(`/tools/cve-lookup/firmware-versions/${modelId}`);
                setVersions(d.versions || []);
                if (d.versions?.length && !version) setVersion(d.versions[0]);
            } catch {/* free-text fallback */}
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modelId]);

    const scan = async () => {
        if (!vendor || !modelName || !version.trim()) {
            toast.error("Pick a vendor, model, and version.");
            return;
        }
        setBusy(true); setResult(null); setError(null);
        try {
            const r = await postJSON("/tools/cve-lookup", { vendor, model: modelName, version: version.trim() });
            setResult(r);
        } catch (err) {
            if (err?.payload?.detail?.error === "rate_limited") {
                setError({ kind: "rate_limited", ...err.payload.detail });
            } else if (err?.status === 503) {
                setError({ kind: "error", message: "NVD database is currently unavailable. Please retry shortly." });
            } else {
                setError({ kind: "error", message: err?.message || "Lookup failed" });
            }
        } finally {
            setBusy(false);
        }
    };

    const unlock = async (e) => {
        e?.preventDefault();
        if (!gateEmail.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(gateEmail.trim())) {
            toast.error("Enter a valid email."); return;
        }
        setUnlocking(true);
        try {
            const r = await postJSON("/tools/cve-lookup", {
                vendor, model: modelName, version: version.trim(),
                email: gateEmail.trim().toLowerCase(),
                name: gateName.trim() || null,
                alerts_optin: gateOptin,
            });
            setResult(r);
            toast.success(`Full report unlocked${gateOptin ? " · alerts on" : ""}`);
        } catch (err) {
            toast.error(err?.message || "Could not unlock");
        } finally {
            setUnlocking(false);
        }
    };

    return (
        <div className="pt-24 sm:pt-32 pb-20 mx-auto max-w-5xl px-5" data-testid="cve-lookup-page">
            <ToolPageMeta toolId="cve-lookup" />
            <Link to="/tools" className="inline-flex items-center gap-1.5 text-[12.5px] text-text-muted hover:text-accent-cyan mb-6">
                <ArrowLeft className="w-3.5 h-3.5" /> Back to tools
            </Link>

            <motion.header initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mb-7">
                <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">Free tool — public</p>
                <h1 className="mt-2 font-display font-semibold text-3xl sm:text-4xl tracking-tight flex items-center gap-3">
                    <AlertTriangle className="w-7 h-7 text-accent-amber" /> Firewall CVE Lookup
                </h1>
                <p className="mt-3 text-[14px] text-text-muted max-w-2xl">
                    Check your firewall firmware against the official <a href="https://nvd.nist.gov" target="_blank" rel="noreferrer" className="text-accent-cyan hover:underline">NVD database</a>. Personalized to the vendors we support in ZeroConf.
                </p>
                {user && (
                    <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-accent-mint/15 border border-accent-mint/40 text-accent-mint text-[11px] font-mono" data-testid="cve-landing-auth-banner">
                        Logged in — full access, no gate.
                    </div>
                )}
            </motion.header>

            {/* 3-step form */}
            <section className="space-y-5 rounded-2xl border border-white/[0.06] bg-surface/60 backdrop-blur-md p-6">
                <div>
                    <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-text-muted mb-2">1. Vendor</p>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                        {VENDOR_CARDS.map((v) => (
                            <button key={v.code} type="button"
                                onClick={() => { setVendor(v.code); setModelId(""); setModelName(""); setVersion(""); }}
                                data-testid={`cve-vendor-${v.code}`}
                                className={[
                                    "px-3 py-2.5 rounded-md border text-[12.5px] font-medium transition-colors",
                                    vendor === v.code ? "bg-accent-cyan/15 border-accent-cyan/50 text-accent-cyan" :
                                    "bg-canvas border-white/[0.08] text-text-muted hover:border-accent-cyan/30",
                                ].join(" ")}>{v.label}</button>
                        ))}
                    </div>
                </div>

                {currentVendorBlock && (
                    <div>
                        <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-text-muted mb-2">2. Model</p>
                        <select value={modelId} onChange={(e) => {
                            const m = currentVendorBlock.models.find((x) => x.id === e.target.value);
                            setModelId(e.target.value); setModelName(m?.name || ""); setVersion("");
                        }} data-testid="cve-model"
                            className="w-full px-3.5 py-2.5 bg-canvas border border-white/[0.08] rounded-md text-[14px] text-text-primary font-mono focus:outline-none focus:border-accent-cyan">
                            <option value="">Select model…</option>
                            {currentVendorBlock.models.map((m) => (
                                <option key={m.id} value={m.id}>{m.displayName}</option>
                            ))}
                        </select>
                    </div>
                )}

                {modelName && (
                    <div>
                        <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-text-muted mb-2">3. Firmware version</p>
                        {versions.length > 0 ? (
                            <select value={version} onChange={(e) => setVersion(e.target.value)} data-testid="cve-version"
                                className="w-full px-3.5 py-2.5 bg-canvas border border-white/[0.08] rounded-md text-[14px] text-text-primary font-mono focus:outline-none focus:border-accent-cyan">
                                {versions.map((v) => <option key={v} value={v}>{v}</option>)}
                            </select>
                        ) : (
                            <input type="text" value={version} onChange={(e) => setVersion(e.target.value)} placeholder="e.g., 7.2.5"
                                data-testid="cve-version-text"
                                className="w-full px-3.5 py-2.5 bg-canvas border border-white/[0.08] rounded-md text-[14px] text-text-primary font-mono placeholder:text-text-dim focus:outline-none focus:border-accent-cyan" />
                        )}
                    </div>
                )}

                <button type="button" onClick={scan} disabled={busy || !vendor || !modelName || !version}
                    data-testid="cve-scan"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-accent-cyan text-canvas text-[13.5px] font-semibold hover:shadow-glow-cyan disabled:opacity-50 transition-all">
                    {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Querying NVD…</> : <><Search className="w-4 h-4" /> Scan</>}
                </button>
            </section>

            {error?.kind === "rate_limited" && (
                <div className="mt-5 rounded-md border border-accent-amber/30 bg-accent-amber/[0.06] p-4" data-testid="cve-rate-limit">
                    <p className="text-[13px] text-accent-amber"><AlertTriangle className="inline w-3.5 h-3.5 mr-1.5" /> You've used your daily {error.limit ?? 5} CVE scans. Resets in {Math.round((error.retry_after_seconds || 0) / 3600)} hours.</p>
                </div>
            )}
            {error?.kind === "error" && (
                <div className="mt-5 rounded-md border border-accent-red/30 bg-accent-red/[0.04] p-4 text-[13px] text-accent-red">
                    <AlertTriangle className="inline w-3.5 h-3.5 mr-1.5" /> {error.message}
                </div>
            )}

            <AnimatePresence>
                {result && (
                    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mt-6 space-y-5" data-testid="cve-results">
                        {/* Summary card */}
                        <div className="rounded-2xl border border-white/[0.06] bg-surface/70 p-6">
                            <div className="flex items-center justify-between flex-wrap gap-3">
                                <div>
                                    <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-text-dim">{result.vendor} · {result.model} · {result.version}</p>
                                    <h2 className="mt-1 font-display font-semibold text-2xl tracking-tight" data-testid="cve-total">
                                        <span className={result.total > 0 ? "text-accent-amber" : "text-accent-mint"}>{result.total}</span> known {result.total === 1 ? "CVE" : "CVEs"}
                                    </h2>
                                </div>
                                <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-md border border-white/[0.08] text-[12.5px] text-text-muted hover:text-accent-cyan">
                                    <FileText className="w-3.5 h-3.5" /> Export PDF
                                </button>
                            </div>
                            <div className="mt-4 flex items-stretch h-3 rounded overflow-hidden border border-white/[0.06]">
                                {["critical", "high", "medium", "low"].map((s) => {
                                    const n = result.severity_counts[s] || 0;
                                    const total = Object.values(result.severity_counts).reduce((a, b) => a + b, 0) || 1;
                                    return <div key={s} style={{ width: `${(n / total) * 100}%`, background: SEV_TONE[s].color }} title={`${s}: ${n}`} />;
                                })}
                            </div>
                            <div className="mt-2 flex flex-wrap gap-3 text-[11px] font-mono">
                                {["critical", "high", "medium", "low"].map((s) => (
                                    <span key={s} className={SEV_TONE[s].text}>{s} {result.severity_counts[s] || 0}</span>
                                ))}
                            </div>
                            <p className="mt-3 text-[10.5px] font-mono text-text-dim">CPE: {result.cpe_query}</p>
                        </div>

                        {/* CVE list */}
                        {result.vulnerabilities.length > 0 ? (
                            <div className="space-y-3" data-testid="cve-list">
                                {user && !result.gated && (
                                    <div className="rounded-md border border-accent-mint/30 bg-accent-mint/[0.04] px-3 py-2 text-[11.5px] text-accent-mint font-mono" data-testid="cve-auth-banner">
                                        Logged in — full access, no gate.
                                    </div>
                                )}
                                {result.vulnerabilities.map((cve) => <CveCard key={cve.cve_id} cve={cve} />)}

                                {/* Email gate (anonymous + gated) */}
                                {result.gated && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                                        className="rounded-2xl border border-accent-cyan/40 bg-gradient-to-br from-accent-cyan/[0.07] to-accent-mint/[0.03] p-5 sm:p-7"
                                        data-testid="cve-email-gate">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-accent-cyan/15 border border-accent-cyan/40 text-accent-cyan">🔓</span>
                                            <h3 className="font-display font-semibold text-[18px]">
                                                Unlock the remaining {Math.max(0, (result.total || 0) - (result.preview_count || 3))} CVEs
                                            </h3>
                                        </div>
                                        <p className="text-[13px] text-text-muted leading-relaxed">
                                            Enter your email to see the full report — including affected sub-versions and patched-in versions.
                                        </p>
                                        <form onSubmit={unlock} className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="cve-email-form">
                                            <input
                                                type="email" required value={gateEmail}
                                                onChange={(e) => setGateEmail(e.target.value)}
                                                placeholder="your@email.com"
                                                data-testid="cve-gate-email"
                                                className="px-3 py-2.5 bg-canvas border border-white/[0.08] rounded-md text-[13.5px] focus:outline-none focus:border-accent-cyan" />
                                            <input
                                                type="text" value={gateName}
                                                onChange={(e) => setGateName(e.target.value)}
                                                placeholder="Your name (optional)"
                                                data-testid="cve-gate-name"
                                                className="px-3 py-2.5 bg-canvas border border-white/[0.08] rounded-md text-[13.5px] focus:outline-none focus:border-accent-cyan" />
                                            <label className="sm:col-span-2 flex items-start gap-2 text-[12.5px] text-text-muted cursor-pointer">
                                                <input type="checkbox" checked={gateOptin}
                                                    onChange={(e) => setGateOptin(e.target.checked)}
                                                    data-testid="cve-gate-optin"
                                                    className="mt-0.5 accent-accent-cyan" />
                                                Subscribe to monthly CVE alerts for {result.vendor} {result.model}.
                                            </label>
                                            <button type="submit" disabled={unlocking}
                                                data-testid="cve-gate-submit"
                                                className="sm:col-span-2 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md bg-accent-cyan text-canvas text-[13.5px] font-semibold hover:shadow-glow-cyan disabled:opacity-50">
                                                {unlocking ? "Unlocking…" : "Get full report →"}
                                            </button>
                                        </form>
                                        <p className="mt-3 text-[10.5px] font-mono text-text-dim">
                                            Currently showing {result.vulnerabilities.length}/{result.total} most-critical CVEs.
                                        </p>
                                    </motion.div>
                                )}

                                {/* Confirmation banner after unlock */}
                                {!result.gated && result.lead_id && (
                                    <div className="rounded-md border border-accent-mint/30 bg-accent-mint/[0.04] px-3 py-2 text-[11.5px] text-accent-mint font-mono" data-testid="cve-unlock-banner">
                                        ✓ Email confirmed · Full list unlocked
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="rounded-xl border border-accent-mint/20 bg-accent-mint/[0.04] p-7 text-center">
                                <ShieldCheck className="w-7 h-7 text-accent-mint mx-auto" />
                                <h3 className="mt-3 font-display font-semibold text-xl">No known CVEs for this combination ✓</h3>
                                <p className="mt-2 text-[13px] text-text-muted">Keep an eye on vendor advisories.</p>
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* FAQ */}
            <section className="mt-12">
                <h2 className="font-display font-semibold text-2xl tracking-tight mb-5">CVE & CVSS — quick reference</h2>
                <div className="space-y-2">
                    <details className="rounded-md border border-white/[0.04] bg-surface/40 p-4 [&_summary::-webkit-details-marker]:hidden">
                        <summary className="cursor-pointer text-[13.5px] font-semibold">What is a CVE?</summary>
                        <p className="mt-3 text-[13px] text-text-muted leading-relaxed">A CVE (Common Vulnerabilities and Exposures) is a public identifier assigned to a specific security flaw in a product. Each CVE gets a description, references, and one or more CVSS scores that quantify exploitability and impact. The NVD (National Vulnerability Database, maintained by NIST) is the authoritative source of CVE data.</p>
                    </details>
                    <details className="rounded-md border border-white/[0.04] bg-surface/40 p-4 [&_summary::-webkit-details-marker]:hidden">
                        <summary className="cursor-pointer text-[13.5px] font-semibold">How should I read the CVSS score?</summary>
                        <p className="mt-3 text-[13px] text-text-muted leading-relaxed">CVSS v3 base score runs 0.0–10.0. Critical = 9.0–10.0 (treat as P0; patch within 48h). High = 7.0–8.9 (P1; patch in next maintenance window). Medium = 4.0–6.9 (P2; plan it). Low = 0.1–3.9 (track). Always factor in environmental context: a critical CVE on an internet-facing management interface is far more urgent than the same CVE on a fully air-gapped lab device.</p>
                    </details>
                    <details className="rounded-md border border-white/[0.04] bg-surface/40 p-4 [&_summary::-webkit-details-marker]:hidden">
                        <summary className="cursor-pointer text-[13.5px] font-semibold">Why do I sometimes see "0 known CVEs"?</summary>
                        <p className="mt-3 text-[13px] text-text-muted leading-relaxed">Three reasons: (1) the firmware version is so new the research community hasn't published CVEs yet — keep watching the vendor advisory feed; (2) the CPE string we generate doesn't match exactly (vendor naming quirks happen); (3) the version is truly clean. If you're on a long-term-support build with active maintenance, "0" is good news.</p>
                    </details>
                </div>
            </section>
        </div>
    );
};

export default CveLookup;
